<script setup lang="ts">
import { useFormDataStore } from '@/entries/interact-form/stores/formData'
import QuestionDisplay from '@/entries/interact-form/components/QuestionDisplay.vue'
import NavigationButtons from '@/entries/interact-form/components/NavigationButtons.vue'

const props = defineProps<{
  hasAnsweredFirstQuestion: boolean
  isModalOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'openModal'): void
  (e: 'updateAnswer', id: number, answer: string | string[]): void
  (e: 'lastQuestionSubmit'): void
}>()

const formDataStore = useFormDataStore()

/**
 * 回答更新のハンドラ
 *
 * 質問への回答が更新されたときに親コンポーネントに通知します
 *
 * @param {number} id - 質問のID
 * @param {string | string[]} answer - 更新する回答（文字列または文字列の配列）
 */
function handleAnswerUpdate(id: number, answer: string | string[]) {
  emit('updateAnswer', id, answer)
}
</script>

<template>
  <div class="interact-form initial-form">
    <h1 class="form-title">カンタン回答で最適なサービスをご提案♪</h1>
    <!-- クリックでモーダルを開くようにする -->
    <div class="question-wrapper" @click="hasAnsweredFirstQuestion && emit('openModal')">
      <QuestionDisplay :question="formDataStore.currentQuestion" :isModal="false" @update:answer="
        (answer) => handleAnswerUpdate(formDataStore.currentQuestion?.id || 0, answer)
      " @auto-next="formDataStore.nextQuestion" />
      <!-- モーダルが閉じられた後で、かつ最初の質問に回答済みの場合にナビゲーションボタンを表示 -->
      <div v-if="hasAnsweredFirstQuestion && !isModalOpen" class="navigation-wrapper">
        <NavigationButtons :is-first-question="formDataStore.currentQuestionIndex === 0"
          :is-last-question="formDataStore.currentQuestionIndex === formDataStore.totalQuestions - 1
            " :is-next-disabled="formDataStore.isNextButtonDisabled"
          @prev="formDataStore.prevQuestion" @next="formDataStore.nextQuestion"
          @submit="emit('lastQuestionSubmit')" />
      </div>
    </div>
    <!-- 通常表示の場合に左下にコンシェルジュ画像を表示 -->
    <div class="top-concierge-container">
      <img src="@/entries/interact-form/img/top_concierge.png" alt="コンシェルジュ"
        class="top-concierge-image" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.initial-form {
  padding: 0 20px;
  border-radius: 8px;
  margin: 20px 0;
  max-width: 900px;
  margin: 0 auto;
  background-color: transparent;
  position: relative;

  & > .question-wrapper > .question-area {
    border: 6px solid #a5dbdf;
    background-color: white;
    max-height: 400px;
    overflow-y: auto;
  }
}

.form-title {
  font-size: 28px;
  font-weight: bold;
  margin-top: 40px;
  margin-bottom: 20px;
  text-align: center;
  padding: 0 48px;
  position: relative;
}

.question-wrapper {
  cursor: pointer;
  transition:
    transform 0.2s,
    box-shadow 0.2s;

  &:hover {
    transform: translateY(-2px);
  }
}

.navigation-wrapper {
  margin-top: 15px;
}

.top-concierge-container {
  position: absolute;
  left: -120px;
  bottom: 30px;
  z-index: 1;
}

.top-concierge-image {
  width: 200px;
  height: auto;
}

@media screen and (max-width: 767px) {
  .initial-form {
    padding: 0 10px;
    margin: 10px 0;
  }

  .form-title {
    font-size: 18px;
    margin-top: 15px;
    margin-bottom: 12px;
    padding: 0 15px;
  }

  .top-concierge-container {
    position: absolute;
    top: 10%;
    left: 10px;
    z-index: 2;
  }

  .top-concierge-image {
    width: 80px;
  }
}
</style>
